import React from 'react';
import PropTypes from 'prop-types';
import { flatten } from 'lodash';

import Loading from '../../../../components/loading';

import {
  createGsheet,
  readGsheet,
  deleteGsheet,
  updateColumn,
  addColumn,
  getColumnIndexName,
} from '../../../../components/gsheet';
import {
  setColumnWidth,
  setDropdown,
} from '../../../../components/gsheet/format';
import UpdateButton from './update-button';
import ContinueButton from './continue-button';
import DialogPrompt from './dialog-prompt';

import {fetchDrugNames} from '../hivdb/load';


import updateData from './update-data';

import {
  getDRMutations,
} from '../../../../utils/QA';

export default class GsheetTable extends React.Component {

  static propTypes = {
    rawDataCollection: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  constructor() {
    super(...arguments);
    this.state = {
      showValidateDialog: false,
      showSubmit: false,
      loadGSheet: false,
      disableButtons: false,
      with_ic50_abs_value: false,
      sheet: "Phenotype"
    };
  }

  set processing(value) {
    if (value) {
      this.setState({
        loadGSheet: true,
        disableButtons: true,
      });
    } else {
      this.setState({
        loadGSheet: false,
        disableButtons: false,
      });
    }
  }

  async componentDidMount() {
    await this.handleCreateGsheet();

    // window.addEventListener('beforeunload',
    //   this.handleDeleteGsheet(this.props.rawDataCollection.spreadsheet_id)
    // );
  }

  get header() {
    const {rawDataCollection} = this.props;
    const isCompleteSequence = rawDataCollection.sequenceType === 'complete';

    let header = [];

    const {seqType} = rawDataCollection;

    if (!seqType || seqType !== 'LabList') {
      header.push('PtAlias');
    }

    header = header.concat([
      'Isolate Name',
      'Gene',
      isCompleteSequence? 'DRMs': 'Mutations',
      'Method',
      'IC',
    ]);

    return header;
  }

  get formats() {
    let result = [];
    result.push(
      setDropdown(
        this.header,
        "IC",
        ['50', '90'],
      )
    );
    result.push(
      setColumnWidth(this.header, 'IC')
    );
    return result;
  }

  getDRMMutations(alignments) {
    return alignments.geneSequences.map((gene) => {
      const geneName = gene.gene;
      const muts = getDRMutations(gene);
      const mutString = muts.map(
        ({shortText}) => {
          return shortText;
        }).join(',');
      return `${geneName}: ${mutString}`;
    }).join('\n');
  }

  getIsolateSequence(ptAlias, isolateName) {

    const {rawDataCollection} = this.props;
    const {rawSequences} = rawDataCollection;

    let sequences = rawSequences.filter((seq) => {
      return (seq.ptAlias === ptAlias && seq.isolateName == isolateName);
    });

    return sequences;
  }

  get table() {
    const {rawDataCollection} = this.props;
    const {seqType} = rawDataCollection;

    if (seqType && seqType === 'LabList') {
      return this.lab_table;
    } else {
      return this.clinical_table;
    }

  }

  get lab_table() {
    const {rawDataCollection} = this.props;
    const {rawSuscResults, rawSequences} = rawDataCollection;

    let table = [];

    let isolateNameSeqMap = {};
    for (const rawSeq of rawSequences) {
      const {isolateName} = rawSeq;
      isolateNameSeqMap[isolateName] = rawSeq;
    }


    for (const susc of rawSuscResults) {
      const rawSeq = isolateNameSeqMap[susc.isolateName];
      const row = [
        rawSeq.isolateName,
        rawSeq.gene,
        rawSeq.mutations,
        susc.method,
        '50',
      ];
      table.push(row);

    }

    table.unshift(this.header);
    return table;
  }

  get clinical_table() {
    const {rawDataCollection} = this.props;
    const {rawSuscResults} = rawDataCollection;
    const {rawAlignmentMap} = rawDataCollection;
    const {sequenceType} = rawDataCollection;

    let table = [];
    const usedIsolateName = [];

    for (const susc of rawSuscResults) {
      const ptAlias = susc['ptAlias'];
      const isolateName = susc['isolateName'];

      if (usedIsolateName.includes(isolateName)) {
        continue;
      } else {
        usedIsolateName.push(isolateName);
      }

      const sequences = this.getIsolateSequence(ptAlias, isolateName);

      let genes = null;
      let mutations = null;
      if (sequenceType !== 'complete') {
        genes = sequences.map((rawSeq) => {
          return rawSeq.gene;
        });
        genes = Array.from(new Set(genes)).join(';');
        mutations = sequences.map((seq) => {
          let muts = seq.mutations;
          if (!muts || muts === '') {
            muts = 'None';
          }
          return `${seq.gene}:${muts}`;
        }).join('\n');
      } else {
        const alignments = sequences.map((seq) => {
          return rawAlignmentMap[seq.uuid];
        });
        genes = Array.from(new Set(flatten(alignments.map((align) => {
          return align.geneSequences.map((gene) => gene.gene);
        })))).join(';');
        mutations = Array.from(new Set(alignments.map((align) => {
          return this.getDRMMutations(align);
        }))).join('\n');
      }

      let row = [
        susc['ptAlias'],
        susc['isolateName'],
        genes,
        mutations,
        susc.method,
        '50',
      ];
      table.push(row);
    }

    table.unshift(this.header);
    return table;
  }

  async handleCreateGsheet() {
    this.processing = true;

    const {table, formats} = this;
    const {rawDataCollection: {references}} = this.props;
    const firstRef = references[0];
    const title = `${firstRef.author}. ` + (
      firstRef.medlineId ? `(PMID: ${firstRef.medlineId})`: ''
    );
    const {sheet} = this.state;

    const {rawDataCollection, onUpdate} = this.props;

    let spreadsheet_id = rawDataCollection.spreadsheet_id;
    let file_info = await createGsheet(
      spreadsheet_id, title, sheet, table, formats);

    if ((
      rawDataCollection.spreadsheet_id != file_info.spreadsheet_id) || (
        rawDataCollection.sheet_id != file_info.sheet_id)) {
      rawDataCollection.spreadsheet_id = file_info.spreadsheet_id;
      rawDataCollection.sheet_id = file_info.sheet_id;
      onUpdate(rawDataCollection);
    }

    this.processing = false;
  }

  handleDeleteGsheet(spreadsheet_id) {
    if (!spreadsheet_id) {
      return null;
    }

    return async () => {
      await deleteGsheet(spreadsheet_id);
    };
  }

  async handleAddColumn(column_name) {
    const {sheet} = this.state;
    await addColumn(this.props.rawDataCollection.spreadsheet_id, sheet, column_name + ' fold');

    const {with_ic50_abs_value} = this.state;
    if (with_ic50_abs_value) {
      await addColumn(this.props.rawDataCollection.spreadsheet_id, sheet, column_name + ' abs value');
    }
    this.setState({with_ic50_abs_value: false});
    return;
  }

  async handleUpdateColumn(column_name, value) {
    const {sheet} = this.state;
    await updateColumn(
      this.props.rawDataCollection.spreadsheet_id,
      sheet,
      getColumnIndexName(this.header, column_name),
      value,
    );
  }

  handleOnCheck(value) {
    this.setState({with_ic50_abs_value: value});
  }

  handleCloseDialog() {
    this.setState({
      showValidateDialog: false
    });
  }

  async handleContinue() {
    const {sheet} = this.state;
    let rows = await readGsheet(this.props.rawDataCollection.spreadsheet_id, sheet);

    const {header} = this;

    let {rawDataCollection} = this.props;
    rawDataCollection = updateData(
      rawDataCollection, rows, header);

    rawDataCollection = await fetchDrugNames(rawDataCollection);

    rawDataCollection.validate();
    if (rawDataCollection.messages('rawSuscResults').length > 0) {
      this.setState({
        showValidateDialog: true,
        showSubmit: false,
      });
    } else {
      this.setState({
        showValidateDialog: true,
        showSubmit: true,
      });
    }
  }

  async handleSubmit() {
    let {rawDataCollection} = this.props;
    const {onUpdate, onSubmit} = this.props;

    await onUpdate(rawDataCollection);

    onSubmit();
  }

  render() {
    if (this.state.loadGSheet) {
      return <Loading />;
    }

    const {showValidateDialog, showSubmit} = this.state;
    const {rawDataCollection} = this.props;

    return (
      <div>
        <UpdateButton
         rawDataCollection={rawDataCollection}
         onAddColumn={
           this.handleAddColumn.bind(this)}
         onUpdateColumn={
           this.handleUpdateColumn.bind(this)}
         onCheck={
           this.handleOnCheck.bind(this)}
        />
        <hr/>
        <ContinueButton
         onSubmit={this.handleContinue.bind(this)}
        />
        <DialogPrompt
         rawDataCollection={rawDataCollection}
         showDialog={showValidateDialog}
         showSubmit={showSubmit}
         onClose={
           this.handleCloseDialog.bind(this)}
         onSubmit={
           this.handleSubmit.bind(this)}
        />
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./src/views/wizard/susc-results/gtable/index.js